import type React from 'react';

import { cn } from '@workspace/ui/lib/utils';

import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardM from '~/components/ProductCardV3/variants/ProductCardM/ProductCardM';
import {
  ProductListAnalyticsProvider,
  useCategoryBlockProductList,
} from '~/components/ProductListAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { CategoryBlockHeader } from './shared/CategoryBlockHeader';

interface ProductsGridMultirowProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
}

export const ProductsGridMultirow: React.FC<ProductsGridMultirowProps> = ({
  category,
  className,
  ...props
}) => {
  const { isMobile } = useGlobal();
  const blockList = useCategoryBlockProductList(category, 'grid');

  // Hide the block when there are no products and none are on the way —
  // home-page categories get their products with the categories tree, so
  // productsRequest may never run and never become fulfilled.
  if (!category.products.length && !category.productsRequest.isLoading) {
    return null;
  }

  if (!category.subcategory.length) {
    return null;
  }

  const products = category.products.slice(0, isMobile ? 10 : 12);

  if (products.length === 0) {
    return (
      <div className="flex gap-4 *:w-[88px] *:shrink-0 md:*:w-[227px]">
        {Array.from({ length: 4 }, (_, i) => (
          <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="m" />
        ))}
      </div>
    );
  }

  return (
    <ProductListAnalyticsProvider list={blockList}>
      <div {...props} className={cn('flex flex-col gap-6', className)}>
        <CategoryBlockHeader category={category} />
        <div
          {...props}
          className={cn(
            'grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4',
            className,
          )}
        >
          {products.map((product, index) => (
            <ProductCardM
              key={product.id}
              product={product}
              index={index}
              className="w-full!"
            />
          ))}
        </div>
      </div>
    </ProductListAnalyticsProvider>
  );
};
